import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Save, AlertCircle, CheckCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const GeneralConfigCRUD = ({ config, onUpdate }) => {
  const [configData, setConfigData] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Collect all config sections except the ones with dedicated components
    const excludedSections = ['hero', 'value_propositions', 'tech_stack', 'custom_models', 'customers', 'email_settings', 'analytics'];
    const generalConfig = {};
    
    Object.keys(config).forEach(section => {
      if (!excludedSections.includes(section)) {
        generalConfig[section] = config[section];
      }
    });
    
    setConfigData(generalConfig);
  }, [config]);

  const handleInputChange = (section, key, value) => {
    setConfigData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasUnsavedChanges(true);
    setSaveStatus('');
  };

  const saveGeneralSettings = async () => {
    setLoading(true);
    setSaveStatus('');
    
    try {
      // Save each config setting
      for (const [section, sectionData] of Object.entries(configData)) {
        for (const [key, value] of Object.entries(sectionData)) {
          await axios.put(`${API_BASE_URL}/api/admin/config/${section}/${key}`, {
            value: value,
            data_type: typeof value === 'object' ? 'json' : 'string'
          }, {
            headers: getAuthHeaders()
          });
        }
      }

      setHasUnsavedChanges(false);
      setSaveStatus('success');
      
      // Reload config data
      await onUpdate();
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving general settings:', error);
      setSaveStatus('error');
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error saving general settings. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const discardChanges = () => {
    if (hasUnsavedChanges && window.confirm('Are you sure you want to discard your changes?')) {
      // Reset to original config
      const excludedSections = ['hero', 'value_propositions', 'tech_stack', 'custom_models', 'customers', 'email_settings', 'analytics'];
      const generalConfig = {};
      
      Object.keys(config).forEach(section => {
        if (!excludedSections.includes(section)) {
          generalConfig[section] = config[section];
        }
      });
      
      setConfigData(generalConfig);
      setHasUnsavedChanges(false);
      setSaveStatus('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center">
          <Settings className="h-5 w-5 text-gray-600 mr-2" />
          <h4 className="text-lg font-semibold text-gray-900">General Configuration</h4>
        </div>
        <p className="text-gray-700 mt-2">
          Configure general website settings and other miscellaneous options.
        </p>
      </div>

      {/* Save Status */}
      {saveStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">General settings saved successfully!</span>
          </div>
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">Error saving general settings. Please try again.</span>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">You have unsaved changes</h4>
                <p className="text-sm text-yellow-700">Save your changes to update the general configuration.</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={discardChanges}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading}
              >
                Discard
              </button>
              <button
                onClick={saveGeneralSettings}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center space-x-2"
                disabled={loading}
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Sections */}
      <div className="space-y-6">
        {Object.entries(configData).map(([section, sectionData]) => (
          <div key={section} className="bg-white rounded-lg shadow-sm p-6">
            <h5 className="font-semibold text-gray-900 mb-4 capitalize">
              {section.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Settings
            </h5>
            
            <div className="space-y-4">
              {Object.entries(sectionData).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                  
                  {typeof value === 'string' ? (
                    value.length > 100 ? (
                      <textarea
                        value={value}
                        onChange={(e) => handleInputChange(section, key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        rows={4}
                        disabled={loading}
                      />
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleInputChange(section, key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        disabled={loading}
                      />
                    )
                  ) : Array.isArray(value) ? (
                    <div className="space-y-2">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Current items:</p>
                        <ul className="space-y-1">
                          {value.map((item, index) => (
                            <li key={`${section}-${key}-${index}`} className="text-sm text-gray-800">
                              {typeof item === 'object' ? item.name || item.title || JSON.stringify(item) : item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-sm text-gray-500">
                        Array values require advanced editing. Use the JSON editor below for complex modifications.
                      </p>
                    </div>
                  ) : (
                    <textarea
                      value={JSON.stringify(value, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          handleInputChange(section, key, parsed);
                        } catch (error) {
                          // Invalid JSON - keep the raw value but show error in UI
                          console.warn('Invalid JSON:', error);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                      rows={6}
                      disabled={loading}
                      placeholder="Enter valid JSON"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {Object.keys(configData).length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No General Settings</h3>
            <p className="text-gray-600">
              All configuration options have dedicated sections. 
              General settings will appear here when they are added to the system.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralConfigCRUD;